// MindForge web — Spanish dictionary (Castilian, also valid for LATAM).
// Must mirror en.ts key-by-key — TypeScript enforces the shape.

import type { Dict } from "../types";

export const es: Dict = {
  // ─── Navegación / compartido ─────────────────────────────────────
  "common.menu": "MENÚ",
  "common.back": "VOLVER AL MENÚ",
  "common.start": "EMPEZAR",
  "common.submit": "Enviar",
  "common.enter": "ENTRAR",
  "common.continue": "Continuar",
  "common.undo": "Deshacer",
  "common.backspace": "Retroceso",
  "common.correct": "¡Correcto!",
  "common.wrong": "¡Incorrecto!",
  "common.hurry": ({ seconds }: { seconds: number }) =>
    `¡Rápido! ${seconds}s`,
  "common.true": "VERDADERO",
  "common.false": "FALSO",
  "common.score": "PUNTOS",
  "common.streak": "RACHA",
  "common.high": "MEJOR",
  "common.match": "COINCIDE",
  "common.matched": "COINCIDÍ",
  "common.getReady": "Prepárate",
  "common.answer": ({ answer }: { answer: string }) =>
    `Respuesta: ${answer}`,

  // ─── Menú principal ──────────────────────────────────────────────
  "menu.header": ({ user }: { user: string }) =>
    `MindForge · Beta · Sesión: ${user}`,
  "menu.levelOfFifty": "/ 50",
  "menu.xpSuffix": "XP",
  "menu.nextXp": ({ amount }: { amount: string }) =>
    `Faltan: ${amount} XP`,
  "menu.sessions": ({ count }: { count: number }) =>
    `${count} sesion${count === 1 ? "" : "es"} jugada${count === 1 ? "" : "s"}`,
  "menu.unlocksAt": ({ level }: { level: number }) =>
    `SE DESBLOQUEA EN EL NIVEL ${level}`,
  "menu.highDiff": ({ high, diff }: { high: number; diff: number }) =>
    `MEJOR · ${high} — DIF ${diff}`,
  "menu.reset": "REINICIAR MI PROGRESO",
  "menu.resetConfirm":
    "¿Reiniciar todo tu progreso?\n\nXP, niveles, dificultades y mejores puntuaciones se borrarán. Esto no se puede deshacer.",
  "menu.betaNotice":
    "BETA · los 7 modos están abiertos desde el nivel 1 para pruebas.",
  "menu.savesNotice":
    "El progreso vive en este dispositivo — borrar los datos del navegador lo elimina. Errores o comentarios → escribe a Jose.",

  // ─── Pantalla de instrucciones (compartida) ──────────────────────
  "instructions.difficulty": "Dificultad",
  "instructions.max": ({ max }: { max: number }) => `MÁX ${max}`,
  "instructions.decreaseDifficulty": "Bajar dificultad",
  "instructions.increaseDifficulty": "Subir dificultad",

  // ─── Pantalla de resultados ──────────────────────────────────────
  "results.sessionComplete": ({ modeName }: { modeName: string }) =>
    `${modeName} · Sesión completada`,
  "results.title": "Resultados",
  "results.score": "Puntos",
  "results.accuracy": "Precisión",
  "results.xpEarned": "XP Ganada",
  "results.bestStreak": "Mejor Racha",
  "results.level": "Nivel",
  "results.progress": "Progreso",
  "results.difficultyUp": "↑ Dificultad aumentada",
  "results.newUnlocks": "Modos nuevos desbloqueados",

  // ─── Metadatos de los modos ──────────────────────────────────────
  "mode.digit_span.name": "Dígitos",
  "mode.digit_span.description": "Recuerda secuencias de dígitos",
  "mode.spatial.name": "Memoria Espacial",
  "mode.spatial.description": "Recuerda las posiciones en orden",
  "mode.dual_task.name": "Tarea Dual",
  "mode.dual_task.description": "Letras y matemáticas a la vez",
  "mode.nback.name": "N-Atrás",
  "mode.nback.description": "Actualiza y sigue secuencias de letras",
  "mode.go_nogo.name": "Go / No-Go",
  "mode.go_nogo.description": "Reacciona rápido, contén el impulso",
  "mode.rule_switch.name": "Cambio de Regla",
  "mode.rule_switch.description": "Adáptate cuando cambian las reglas",
  "mode.chunking.name": "Reto de Agrupamiento",
  "mode.chunking.description": "Domina el agrupamiento de la memoria",

  // ─── Dígitos ─────────────────────────────────────────────────────
  "digitSpan.memorize": "Memoriza",
  "digitSpan.typeForward": "Escribe la secuencia",
  "digitSpan.typeReverse": "Escribe la secuencia al REVÉS",
  "digitSpan.instr.show": ({ n }: { n: number }) =>
    `Aparecerá una secuencia de ${n} dígitos, uno a uno.`,
  "digitSpan.instr.watch":
    "Observa con atención y memoriza el orden.",
  "digitSpan.instr.reverse":
    "Después escríbelos al REVÉS — el último primero.",
  "digitSpan.instr.forward":
    "Después escríbelos en el orden correcto.",
  "digitSpan.instr.keyboard":
    "Pulsa ENTRAR para enviar, RETROCESO para corregir. El teclado físico también funciona.",
  "digitSpan.diffDesc": ({ n, reverse }: { n: number; reverse: boolean }) =>
    `Secuencias de ${n} dígitos · ${reverse ? "orden INVERSO" : "orden directo"}`,

  // ─── Memoria Espacial ────────────────────────────────────────────
  "spatial.watch": "Mira la secuencia",
  "spatial.tap": "Toca las celdas en orden",
  "spatial.cellLabel": ({ row, col }: { row: number; col: number }) =>
    `Fila ${row}, columna ${col}`,
  "spatial.instr.watch": ({ cells, grid }: { cells: number; grid: number }) =>
    `Verás ${cells} celdas iluminarse en una cuadrícula de ${grid}×${grid}.`,
  "spatial.instr.tap":
    "Cuando termine la secuencia, toca las mismas celdas en el mismo orden.",
  "spatial.instr.undo":
    "Puedes deshacer el último toque. Envía cuando tengas todas.",
  "spatial.instr.threshold":
    "Precisión del 70% o más cuenta como correcto.",
  "spatial.diffDesc": ({ grid, cells }: { grid: number; cells: number }) =>
    `Cuadrícula ${grid}×${grid} · ${cells} celdas`,

  // ─── Tarea Dual ──────────────────────────────────────────────────
  "dualTask.rememberLetter": "Recuerda esta letra",
  "dualTask.letterOf": ({ index, total }: { index: number; total: number }) =>
    `Letra ${index} de ${total}`,
  "dualTask.trueOrFalse": "¿Verdadero o falso?",
  "dualTask.wrongMath": ({ penalty }: { penalty: number }) =>
    `¡Incorrecto! −${penalty} XP`,
  "dualTask.recall": "Escribe las letras en orden",
  "dualTask.mathScore": ({ correct, total }: { correct: number; total: number }) =>
    `Matemáticas: ${correct}/${total} correctas`,
  "dualTask.instr.show": ({ n }: { n: number }) =>
    `Verás ${n} letras, una a una. Memoriza cada una.`,
  "dualTask.instr.math":
    "Después de cada letra aparece un problema de matemáticas — responde VERDADERO o FALSO.",
  "dualTask.instr.penalty":
    "Las respuestas matemáticas erróneas cuestan XP al instante.",
  "dualTask.instr.recall":
    "Al final, escribe las letras en orden. Las letras deben ser correctas Y las matemáticas ≥ 50%.",
  "dualTask.diffDesc": ({ n }: { n: number }) =>
    `${n} parejas letra–matemática`,

  // ─── N-Atrás ─────────────────────────────────────────────────────
  "nback.modeTitle": ({ n }: { n: number }) => `${n}-Atrás`,
  "nback.matchHint": ({ n }: { n: number }) =>
    `Pulsa COINCIDE si esta letra apareció hace ${n} paso${n === 1 ? "" : "s"}.`,
  "nback.instr.appear": ({ n }: { n: number }) =>
    `Las letras aparecen una a una. Pulsa COINCIDE cuando la letra actual sea igual a la de hace ${n} paso${n === 1 ? "" : "s"}.`,
  "nback.instr.firstN":
    "Las primeras N letras nunca pueden coincidir — todavía no hay con qué compararlas.",
  "nback.instr.threshold":
    "Precisión del 70% para aprobar. N se ajusta entre sesiones según cómo lo hagas.",
  "nback.diffDesc": ({ n, count }: { n: number; count: number }) =>
    `N = ${n} · ${count} estímulos`,
  "nback.sequenceComplete": "¡Secuencia completada!",
  "nback.sequenceFailed": "¡Secuencia fallida!",
  "nback.accuracy": ({ pct }: { pct: number }) => `Precisión: ${pct}%`,
  "nback.hits": "aciertos",
  "nback.misses": "fallos",
  "nback.falseAlarms": "falsas",
  "nback.nLevelChange": ({ from, to }: { from: number; to: number }) =>
    `Nivel-N: ${from} → ${to}`,

  // ─── Go / No-Go ──────────────────────────────────────────────────
  "goNoGo.go": "GO",
  "goNoGo.stop": "STOP",
  "goNoGo.tapHint":
    "Toca en cualquier parte de la pantalla solo para las figuras GO.",
  "goNoGo.hits": "ACIERTOS",
  "goNoGo.missesShort": "FALLOS",
  "goNoGo.falseAlarms": "FA",
  "goNoGo.instr.go":
    "Las figuras GO son círculos verdes. Toca lo más rápido que puedas.",
  "goNoGo.instr.noGo":
    "Las figuras NO-GO son cuadrados rojos. NO las toques.",
  "goNoGo.instr.run":
    "Cada ronda tiene 20+ estímulos. 70% de precisión para aprobar.",
  "goNoGo.instr.pace":
    "El ritmo se acelera cuando sube la dificultad.",
  "goNoGo.diffDesc": ({ count, pct }: { count: number; pct: number }) =>
    `${count} estímulos · ${pct}% de tasa nogo`,
  "goNoGo.sequenceComplete": "¡Secuencia completada!",
  "goNoGo.sequenceFailed": "¡Secuencia fallida!",

  // ─── Cambio de Regla ─────────────────────────────────────────────
  "ruleSwitch.streak": ({ n }: { n: number }) => `Racha: ${n}`,
  "ruleSwitch.target": ({ index }: { index: number }) =>
    `Objetivo ${index}`,
  "ruleSwitch.tapHint": "Toca una carta objetivo para clasificar.",
  "ruleSwitch.hint.color": "Piensa en COLORES…",
  "ruleSwitch.hint.shape": "Fíjate en las FORMAS…",
  "ruleSwitch.hint.count": "Cuenta los elementos…",
  "ruleSwitch.instr.targets":
    "Hay 4 cartas objetivo arriba y una carta actual abajo.",
  "ruleSwitch.instr.sort":
    "Clasifica la carta actual en un objetivo según una regla — color, forma o cantidad.",
  "ruleSwitch.instr.figureOut":
    "La regla no se muestra; dedúcela por las respuestas.",
  "ruleSwitch.instr.switch": ({ n }: { n: number }) =>
    `Tras ${n} aciertos seguidos, la regla cambia en silencio.`,
  "ruleSwitch.diffDesc": ({ n }: { n: number }) =>
    `La regla cambia cada ${n} aciertos`,

  // ─── Reto de Agrupamiento ────────────────────────────────────────
  "chunking.memorize": "Memoriza esta secuencia",
  "chunking.seeChunks": "Ahora mira los grupos",
  "chunking.enterFull": "Escribe la secuencia completa",
  "chunking.instr.study": ({ n }: { n: number }) =>
    `Estudia una secuencia bruta de ${n} caracteres durante unos segundos.`,
  "chunking.instr.chunks":
    "Luego verás la misma secuencia agrupada en bloques de color.",
  "chunking.instr.recall":
    "Por último, escribe la secuencia completa. 70% de precisión para aprobar.",
  "chunking.instr.why":
    "El agrupamiento es la técnica de memoria más útil que existe.",
  "chunking.diffDesc": ({ length, size }: { length: number; size: number }) =>
    `Secuencia de ${length} · grupos de ${size}`,
  "chunking.tip.0":
    "Agrupa elementos en patrones con sentido (como números de teléfono).",
  "chunking.tip.1":
    "Busca palabras o abreviaturas dentro de los grupos de letras.",
  "chunking.tip.2":
    "Asocia cada grupo con una imagen mental vívida.",
  "chunking.tip.3":
    "Usa el ritmo: di cada grupo como un 'tiempo' en una canción.",
  "chunking.tip.4":
    "Crea historias que unan tus grupos.",
  "chunking.tip.5":
    "Busca patrones numéricos: ascendentes, descendentes o repetidos.",

  // ─── Selector de idioma ──────────────────────────────────────────
  "locale.label": "Idioma",
  "locale.en": "EN",
  "locale.es": "ES",

  // ─── Selector de tema ─────────────────────────────────────────────
  "theme.label": "Tema",
  "theme.light": "Blanco",
  "theme.dark": "Oscuro",

  // ─── Bienvenida inicial ───────────────────────────────────────────
  "onboarding.skip": "Saltar",
  "onboarding.next": "Siguiente",
  "onboarding.start": "Empezar",
  "onboarding.welcome.title": "Bienvenido a MindForge",
  "onboarding.welcome.body":
    "Un gimnasio para la memoria de trabajo — el búfer mental detrás de la concentración, la lectura y la resolución de problemas. Sesiones cortas a diario, sin configuración.",
  "onboarding.how.title": "Cómo funciona",
  "onboarding.how.body":
    "Empieza con Dígitos. Juega unos minutos al día. Alcanza el 80% de precisión y la dificultad sube para ajustarse a ti. Gana XP para desbloquear seis modos más.",
  "onboarding.expect.title": "Qué esperar",
  "onboarding.expect.body":
    "Mejorarás de forma medible en estos ejercicios. No prometemos hacerte más inteligente en general — solo un entrenamiento preciso para una habilidad precisa.",

};
